import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import GiftItemPreview from '@service/giftStore/components/GiftItemPreview';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { giftDeliveryCategoryListDataAtom } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import { trackGiftEvent } from '@shared/api/giftEventLog/giftEventLog.controller';
import {
  giftItemListForStoreDataAtomFamily,
  giftItemListForStoreStatusAtomFamily,
} from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { useRedirect } from '@shared/hooks/useRedirect';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import _ from 'lodash';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import giftPromStoreMain from 'src/assets/gift/gift-prom-store-main.png';

import { GiftCallHelperBox } from '../components/GiftCallHelperBox';
import GiftStoreFooter from '../components/GiftStoreFooter';

const GiftStoreMainPage: FC = function GiftStoreMainPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const method = searchParams.get('method');
  const fromAlimTalk = method === 'kakao';

  useRedirect({ skip: !fromAlimTalk });

  useEffect(() => {
    if (fromAlimTalk) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('method');
      setSearchParams(nextParams, { replace: true });
    }
  }, [fromAlimTalk, searchParams, setSearchParams]);

  const paramsString = searchParams.toString();

  useEffect(() => {
    if (_.isNull(bugoId)) {
      navigate('/');
    } else {
      trackGiftEvent('store_view', bugoId);
    }
  }, [bugoId, navigate]);

  useGiftItemListForStoreHook(bugoId ?? '');
  const status = useRecoilValue(giftItemListForStoreStatusAtomFamily(bugoId ?? ''));
  const data = useRecoilValue(giftItemListForStoreDataAtomFamily(bugoId ?? ''));

  useGiftDeliveryCategoryListHook(LowerRole.Public);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);
  const getImageUrl = (item: GiftItemForStore): string => {
    const image = item.image || item.giftItemInfo?.image;
    if (image && typeof image === 'object' && 'url' in image) {
      return fileBucketUrl((image as Image).url, 'w400');
    }
    return '';
  };

  const getName = (item: GiftItemForStore): string => {
    return item.name || item.giftItemInfo?.name || '';
  };

  const getType = (item: GiftItemForStore): GiftItemInfoType => {
    return item.giftItemInfo?.type || GiftItemInfoType.Delivery;
  };

  const getTags = (item: GiftItemForStore): string[] => {
    return item.giftItemInfo?.tags || [];
  };

  const getIsSelectable = (item: GiftItemForStore): boolean => {
    return item.giftItemInfo?.isSelectable || false;
  };

  const getChoiceUnitCount = (item: GiftItemForStore): number => {
    const units = item.giftItemInfo?.choiceUnits;
    if (!units || !Array.isArray(units)) return 0;
    return units.filter(
      (u): u is GiftChoiceUnitData => typeof u === 'object' && u !== null,
    ).length;
  };

  const getBrandName = (item: GiftItemForStore): string | undefined => {
    const brand = item.giftItemInfo?.giftItemBrand;
    if (brand && typeof brand === 'object' && 'name' in brand) {
      return brand.name;
    }
    return undefined;
  };

  const getDisplayOrder = (item: GiftItemForStore): number => {
    if (item.order !== undefined) return item.order;
    if (item.giftItemInfo?.order !== undefined) return item.giftItemInfo.order;
    return Number.MAX_SAFE_INTEGER;
  };

  const getCategoryId = (item: GiftItemForStore): string | undefined => {
    const override = item.giftDeliveryCategory;
    if (override) {
      return typeof override === 'object' ? override._id : override;
    }
    const fromInfo = item.giftItemInfo?.giftDeliveryCategory;
    if (fromInfo) {
      return typeof fromInfo === 'object' ? fromInfo._id : fromInfo;
    }
    return undefined;
  };

  // 상품이 선택한 하위분류 이름 (원상품에만 존재)
  const getSubCategory = (item: GiftItemForStore): string | undefined => {
    return item.giftItemInfo?.subCategory || undefined;
  };

  const sortedData = useMemo(() => {
    if (!data) return null;
    return [...data]
      .filter((item) => item.giftItemInfo?.isEnabled !== false)
      .sort((a, b) => {
        const typeA = getType(a) === GiftItemInfoType.Coupon ? 0 : 1;
        const typeB = getType(b) === GiftItemInfoType.Coupon ? 0 : 1;
        if (typeA !== typeB) return typeA - typeB;
        return getDisplayOrder(a) - getDisplayOrder(b);
      });
  }, [data]);

  // 배송형 탭 / 카테고리 행으로 스크롤 이동시키기 위한 ref
  const typeTabRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);

  const scrollToTop = (el: HTMLElement | null) => {
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const [typeFilter, setTypeFilter] = useState<'Coupon' | 'Delivery'>('Coupon');
  // 카테고리(대분류) + 하위분류 이름 2단계 필터.
  // categoryFilter=null 이면 전체, subFilter=null 이면 해당 카테고리 전체
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [subFilter, setSubFilter] = useState<string | null>(null);

  const typeCounts = useMemo(() => {
    if (!sortedData) return { Coupon: 0, Delivery: 0 };
    return {
      Coupon: sortedData.filter((item) => getType(item) === GiftItemInfoType.Coupon)
        .length,
      Delivery: sortedData.filter((item) => getType(item) === GiftItemInfoType.Delivery)
        .length,
    };
  }, [sortedData]);

  const hasBothTypes = typeCounts.Coupon > 0 && typeCounts.Delivery > 0;

  const filteredData = useMemo(() => {
    if (!sortedData) return null;
    const byType = !hasBothTypes
      ? sortedData
      : sortedData.filter(
          (item) =>
            getType(item) ===
            (typeFilter === 'Coupon'
              ? GiftItemInfoType.Coupon
              : GiftItemInfoType.Delivery),
        );
    const isDeliveryActive = !hasBothTypes
      ? typeCounts.Delivery > 0 && typeCounts.Coupon === 0
      : typeFilter === 'Delivery';
    if (!isDeliveryActive || !categoryFilter) return byType;
    return byType.filter((item) => {
      if (getCategoryId(item) !== categoryFilter) return false;
      // 하위분류 선택 시: 같은 카테고리 + 해당 하위분류 이름만
      if (subFilter) return getSubCategory(item) === subFilter;
      return true;
    });
  }, [sortedData, typeFilter, hasBothTypes, typeCounts, categoryFilter, subFilter]);

  // 배송형 상품이 실제로 존재하는 카테고리 id 집합
  const usedCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    (sortedData ?? []).forEach((item) => {
      if (getType(item) !== GiftItemInfoType.Delivery) return;
      const id = getCategoryId(item);
      if (id) ids.add(id);
    });
    return ids;
  }, [sortedData]);

  // 카테고리 퀵필터: 배송형 상품이 있는 카테고리만 노출
  const categoryOptions = useMemo(() => {
    return deliveryCategoryList.filter((c) => usedCategoryIds.has(c._id));
  }, [deliveryCategoryList, usedCategoryIds]);

  // 선택된 카테고리의 하위분류 중 실제로 상품이 있는 이름만 노출
  const subCategoryOptions = useMemo(() => {
    if (!categoryFilter) return [];
    const category = deliveryCategoryList.find((c) => c._id === categoryFilter);
    const names = category?.subCategories ?? [];
    if (names.length === 0) return [];
    const usedNames = new Set<string>();
    (sortedData ?? []).forEach((item) => {
      if (getType(item) !== GiftItemInfoType.Delivery) return;
      if (getCategoryId(item) !== categoryFilter) return;
      const sub = getSubCategory(item);
      if (sub) usedNames.add(sub);
    });
    return names.filter((name) => usedNames.has(name));
  }, [categoryFilter, deliveryCategoryList, sortedData]);

  const isDeliveryViewActive = hasBothTypes
    ? typeFilter === 'Delivery'
    : typeCounts.Delivery > 0 && typeCounts.Coupon === 0;

  // 타입 전환 시 카테고리 필터 초기화
  useEffect(() => {
    if (!isDeliveryViewActive) {
      if (categoryFilter) setCategoryFilter(null);
      if (subFilter) setSubFilter(null);
    }
  }, [isDeliveryViewActive, categoryFilter, subFilter]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 히어로 배너 */}
      <div>
        <img src={giftPromStoreMain} alt="답례품 안내" className="w-full" />
      </div>

      {/* 상품 목록 */}
      {status === 'success' && filteredData ? (
        sortedData && sortedData.length > 0 ? (
          <div className="px-3 pb-4 pt-3">
            {/* 타입 필터 탭 */}
            {hasBothTypes && (
              <div
                ref={typeTabRef}
                className="mb-3 scroll-mt-16 overflow-hidden rounded-full bg-gray-200 shadow-sm ring-1 ring-gray-200"
              >
                <div className="grid grid-cols-2 gap-px">
                  {[
                    { key: 'Coupon' as const, label: '쿠폰형' },
                    { key: 'Delivery' as const, label: '배송형' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setTypeFilter(tab.key);
                        // 배송형 전환 시 쿠폰형 배너를 지나 탭 바가 맨 위에 오도록 스크롤
                        if (tab.key === 'Delivery') scrollToTop(typeTabRef.current);
                      }}
                      className={`py-2.5 text-sm font-semibold transition-colors ${
                        typeFilter === tab.key
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-500 active:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 배송 카테고리 퀵필터 (배송형일 때만) — 카테고리(원형) → 하위분류(칩) 2단계 */}
            {isDeliveryViewActive && categoryOptions.length > 0 && (
              <div ref={categoryRef} className="mb-4 scroll-mt-16">
                {/* 카테고리 행 */}
                <div className="-mx-3 overflow-x-auto hide-scrollbar">
                  <div className="flex min-w-full justify-evenly gap-4 px-3 pb-1 pt-1">
                    {categoryOptions.map((category) => {
                      const isActive = categoryFilter === category._id;
                      const imageUrl =
                        category.image && typeof category.image === 'object'
                          ? fileBucketUrl(category.image.url, 'raw')
                          : '';
                      return (
                        <button
                          key={category._id}
                          onClick={() => {
                            // 같은 카테고리 재클릭 시 해제, 아니면 선택 후 하위분류 초기화
                            setCategoryFilter(isActive ? null : category._id);
                            setSubFilter(null);
                            // 카테고리 선택 시 카테고리 행이 맨 위에 오도록 스크롤
                            // (하위분류 + 많은 물품을 바로 볼 수 있게)
                            if (!isActive) scrollToTop(categoryRef.current);
                          }}
                          className="flex w-[4.5rem] shrink-0 flex-col items-center gap-1.5"
                        >
                          <div
                            className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-all ${
                              isActive ? 'ring-2 ring-offset-2' : ''
                            }`}
                          >
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={category.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="h-full w-full bg-gray-100" />
                            )}
                          </div>
                          <span
                            className={`truncate text-13 font-semibold ${
                              isActive ? 'theme-text-main' : 'text-gray-800'
                            }`}
                          >
                            {category.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 소분류 행 (대분류 선택 + 해당 대분류에 소분류가 있을 때만) */}
                {subCategoryOptions.length > 0 && (
                  <div className="-mx-3 mt-3 overflow-x-auto hide-scrollbar">
                    <div className="flex min-w-full gap-2 px-3 pb-1 pt-1">
                      <button
                        onClick={() => setSubFilter(null)}
                        className={`shrink-0 rounded-full px-3.5 py-1.5 text-13 font-semibold transition-colors ${
                          subFilter === null
                            ? 'theme-bg-main text-white'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        전체
                      </button>
                      {subCategoryOptions.map((sub) => {
                        const isActive = subFilter === sub;
                        return (
                          <button
                            key={sub}
                            onClick={() => setSubFilter(isActive ? null : sub)}
                            className={`shrink-0 rounded-full px-3.5 py-1.5 text-13 font-semibold transition-colors ${
                              isActive
                                ? 'theme-bg-main text-white'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {sub}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 상품 그리드 */}
            <div className="grid grid-cols-2 gap-2.5">
              {filteredData.map((item, idx) => (
                <Link
                  key={`gift-item-link-${idx}`}
                  to={`/gift-store/item/${item._id}?${paramsString}`}
                  state={{ name: getName(item) }}
                >
                  <GiftItemPreview
                    image={getImageUrl(item)}
                    name={getName(item)}
                    priceRegular={item.priceRegular}
                    priceRetail={item.priceRetail}
                    type={getType(item)}
                    tags={getTags(item)}
                    isSelectable={getIsSelectable(item)}
                    choiceUnitCount={getChoiceUnitCount(item)}
                    brandName={getBrandName(item)}
                  />
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="center-box h-screen-15 flex-col gap-2 text-gray-500">
            <p>등록된 답례품이 없습니다.</p>
          </div>
        )
      ) : (
        <div className="center-box h-screen-15">
          <Loading />
        </div>
      )}
      <GiftCallHelperBox />
      <GiftStoreFooter />
    </div>
  );
};

export default GiftStoreMainPage;
